import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";

const AdminCustomers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(searchParams.get("filter") || "all");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get("/api/admin/customers");
        setRows(response.data?.customers || []);
      } catch (err) {
        setError(err.response?.data?.error || "Failed to load customers");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    setStateFilter(searchParams.get("state") || "all");
    setSearch(searchParams.get("q") || "");
    setCustomerFilter(searchParams.get("filter") || "all");
    setSort(searchParams.get("sort") || "newest");
  }, [searchParams]);

  const setUrlState = (patch) => {
    const next = { state: stateFilter, q: search, filter: customerFilter, sort, ...patch };
    const params = new URLSearchParams();
    if (next.state && next.state !== "all") params.set("state", next.state);
    if (next.q && next.q.trim()) params.set("q", next.q.trim());
    if (next.filter && next.filter !== "all") params.set("filter", next.filter);
    if (next.sort && next.sort !== "newest") params.set("sort", next.sort);
    setSearchParams(params);
  };

  const stateOptions = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((row) => String(row.customer?.state || "").trim())
            .filter(Boolean)
        )
      ).sort(),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      const customer = row.customer || {};
      const matchesState =
        stateFilter === "all" || String(customer.state || "").trim() === stateFilter;
      const matchesCustomerFilter =
        customerFilter === "all" ||
        (customerFilter === "paid" && Number(row.paidOrderCount || 0) > 0) ||
        (customerFilter === "no-paid" && Number(row.paidOrderCount || 0) === 0);
      const haystack = [
        customer.name,
        customer.email,
        customer.phone,
        customer.address,
        customer.state,
        customer.pincode,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      return matchesState && matchesSearch && matchesCustomerFilter;
    });
    return [...filtered].sort((a, b) => {
      if (sort === "spend-high") return Number(b.totalSpend || 0) - Number(a.totalSpend || 0);
      if (sort === "orders-high") return Number(b.orderCount || 0) - Number(a.orderCount || 0);
      if (sort === "last-order") return new Date(b.lastOrderAt || 0) - new Date(a.lastOrderAt || 0);
      if (sort === "state") return String(a.customer?.state || "").localeCompare(String(b.customer?.state || ""));
      return new Date(b.customer?.createdAt || 0) - new Date(a.customer?.createdAt || 0);
    });
  }, [customerFilter, rows, search, sort, stateFilter]);

  const summary = useMemo(
    () => ({
      customers: filteredRows.length,
      paidCustomers: filteredRows.filter((row) => Number(row.paidOrderCount || 0) > 0).length,
      spend: filteredRows.reduce((sum, row) => sum + Number(row.totalSpend || 0), 0),
    }),
    [filteredRows]
  );

  if (loading) return <div style={{ padding: 24 }}>Loading customers...</div>;
  if (error) return <div style={{ padding: 24, color: "red" }}>{error}</div>;

  return (
    <div style={{ padding: 24, display: "grid", gap: 18 }}>
      <div>
        <h2 style={{ margin: 0 }}>Customer Reports</h2>
        <p style={{ margin: "6px 0 0", color: "#64748b" }}>
          Track customer contact details, order count, spend, and last activity.
        </p>
      </div>

      <div style={summaryGrid}>
        {[
          ["Customers", summary.customers],
          ["With Paid Orders", summary.paidCustomers],
          ["Total Spend", `Rs.${Number(summary.spend || 0).toFixed(2)}`],
        ].map(([label, value]) => (
          <button
            key={label}
            type="button"
            style={summaryCard}
            onClick={() => {
              if (label === "Customers") setUrlState({ filter: "all" });
              if (label === "With Paid Orders") setUrlState({ filter: "paid" });
            }}
          >
            <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, marginTop: 8 }}>{value}</div>
          </button>
        ))}
      </div>

      <div style={toolbar}>
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setUrlState({ q: event.target.value });
          }}
          placeholder="Search name, email, phone, address..."
          style={input}
        />
        <select value={customerFilter} onChange={(event) => setUrlState({ filter: event.target.value })} style={input}>
          <option value="all">All Customers</option>
          <option value="paid">Customers with paid orders</option>
          <option value="no-paid">No paid orders yet</option>
        </select>
        <select value={stateFilter} onChange={(event) => setUrlState({ state: event.target.value })} style={input}>
          <option value="all">All States</option>
          {stateOptions.map((state) => (
            <option key={state} value={state}>
              {state}
            </option>
          ))}
        </select>
        <select value={sort} onChange={(event) => setUrlState({ sort: event.target.value })} style={input}>
          <option value="newest">Newest customers</option>
          <option value="spend-high">Highest spend</option>
          <option value="orders-high">Most orders</option>
          <option value="last-order">Recently ordered</option>
          <option value="state">State A-Z</option>
        </select>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={table}>
          <thead>
            <tr style={{ background: "#f8fafc", textAlign: "left" }}>
              <th style={th}>Customer</th>
              <th style={th}>Contact</th>
              <th style={th}>Location</th>
              <th style={th}>Orders</th>
              <th style={th}>Paid Orders</th>
              <th style={th}>Total Spend</th>
              <th style={th}>Last Order</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => {
              const customer = row.customer || {};
              return (
                <tr key={customer._id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={td}>
                    <strong>{customer.name || "Customer"}</strong>
                    <div style={muted}>Joined {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : "-"}</div>
                  </td>
                  <td style={td}>
                    <div>{customer.email || "No email"}</div>
                    <div style={muted}>{customer.phone || "No phone"}</div>
                  </td>
                  <td style={td}>
                    {[customer.address, customer.state, customer.pincode].filter(Boolean).join(", ") || "-"}
                  </td>
                  <td style={td}>{row.orderCount || 0}</td>
                  <td style={td}>{row.paidOrderCount || 0}</td>
                  <td style={td}>Rs.{Number(row.totalSpend || 0).toFixed(2)}</td>
                  <td style={td}>{row.lastOrderAt ? new Date(row.lastOrderAt).toLocaleString() : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredRows.length === 0 ? <p>No customers found for this filter.</p> : null}
    </div>
  );
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: 900,
  background: "#fff",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
};
const th = { padding: "12px 16px", borderBottom: "2px solid #e2e8f0" };
const td = { padding: "12px 16px", verticalAlign: "top" };
const muted = { fontSize: 12, color: "#64748b", marginTop: 4 };
const toolbar = {
  display: "grid",
  gridTemplateColumns: "minmax(260px, 1fr) repeat(3, minmax(170px, 220px))",
  gap: 12,
};
const input = {
  padding: "10px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  background: "#fff",
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
};
const summaryCard = {
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

export default AdminCustomers;
